import type { ReactNode } from "react";

import { Icon } from "@iconify-icon/react";

import { cn } from "@/lib/utils";

interface SettingsCardProps {
  title?: string | ReactNode;
  description?: string;
  icon?: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  bottomCircle?: string;
  topCircle?: string;
}

export function SettingsCard({
  title,
  description,
  icon,
  children,
  action,
  className,
  bottomCircle = "from-pink-500 to-teal-600",
  topCircle = "from-cyan-600 to-purple-400",
}: SettingsCardProps) {
  return (
    <div
      className={cn(
        "bg-foreground/50 text-accent-foreground relative z-50 rounded-2xl border-none p-4 sm:p-8 shadow-2xl backdrop-blur-xl max-[400px]:p-2",
        className,
      )}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl opacity-5">
        {/* Changed colors and position for the first circle */}
        <div
          className={`absolute -top-20 -left-20 h-32 w-32 rounded-full bg-gradient-to-br ${topCircle}`}
        />
        {/* Changed colors and position for the second circle */}
        <div
          className={`absolute -right-20 -bottom-20 h-40 w-40 rounded-full bg-gradient-to-tr ${bottomCircle}`}
        />
      </div>

      <div className="relative">
        {/* Mobile header: icon next to title, description below */}
        <div className="mb-4 sm:mb-6 sm:hidden">
          <div className="flex items-center gap-3">
            {icon && (
              <div className="bg-muted/20 flex h-10 w-10 items-center justify-center rounded-xl max-sm:bg-transparent">
                <Icon className="text-accent-foreground" height={"1.25rem"} icon={icon} width={"1.25rem"} />
              </div>
            )}
            <h3 className="text-accent-foreground text-lg font-semibold">{title}</h3>
          </div>
          {description && (
            <p className="text-muted-foreground mt-2 text-sm">{description}</p>
          )}
          {action && <div className="mt-3">{action}</div>}
        </div>

        {/* Desktop/tablet header: icon left, stacked title+description to the right */}
        <div className="mb-6 hidden items-start justify-between sm:flex">
          <div className="flex items-start gap-4">
            {icon && (
              <div className="bg-muted/20 flex h-14 w-14 items-center justify-center rounded-2xl backdrop-blur-sm">
                <Icon className="text-accent-foreground" height={"1.75rem"} icon={icon} width={"1.75rem"} />
              </div>
            )}
            <div>
              <h3 className="text-accent-foreground text-xl font-semibold">{title}</h3>
              {description && (
                <p className="text-muted-foreground mt-2 text-sm">{description}</p>
              )}
            </div>
          </div>
          {action && <div className="flex-shrink-0">{action}</div>}
        </div>

        <div className="mt-4 sm:mt-6">{children}</div>
      </div>
    </div>
  );
}

